import { Injectable } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { Map, Set } from 'immutable';
import { map, Observable } from 'rxjs';

import { ElementPermission } from '../../core/domain/permission-types';
import { SubscriptionItemEntity } from '../components/subscription-list/subscription-item.entity';
import { ConsumedProjectItemEntity } from '../components/subscription-view/consumed-project-item.entity';
import { ProjectItemEntity } from '../components/subscription-view/consumer-config-modal/project-item.entity';
import { HistoryItemEntity } from '../components/subscription-view/history-list-modal/history-item.entity';
import { MemberAccessEntity } from '../components/subscription-view/permissions-config-modal/member-access.entity';
import { PublishedSubscriptionEntity } from '../components/subscription-view/published-subscription.entity';
import { ViewDetailsEntity } from '../components/subscription-view/view-details.entity';
import { SwitchableVersionProjectItemEntity } from '../components/version-switcher-modal/switchable-version-project-item.entity';
import { ColumnConfigItemEntity } from '../domain/column-config-item.entity';
import { ColumnModelItemEntity } from '../domain/column-model-item.entity';
import { FilterModelItemEntity } from '../domain/filter-model-item.entity';
import { InternalMetadataEntity } from '../domain/internal-metadata.entity';
import { ProfileItemEntity } from '../domain/profile-item.entity';
import { PublicMetadataEntity } from '../domain/public-metadata.entity';
import {
	SubscriptionAction,
	SubscriptionStatus,
} from '../domain/subscription.types';
import { featureSelector, SubscriptionRootState } from './feature.store';
import {
	SubscriptionOriginalSliceState,
	SubscriptionSliceKey,
} from './subscription.state';

const baseSelector = createSelector(
	featureSelector,
	(state) => state[SubscriptionSliceKey]
);

const SubscriptionColumns = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').availableColumns
);

const SubscriptionProjects = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').consumedProjects
);

const SubscriptionSwitchableProjects = createSelector(baseSelector, (state) =>
	state.get('switchableConsumedProjects')
);

const SubscriptionProfiles = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').profiles
);

const SubscriptionColumnModels = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').columnModels
);

const SubscriptionFilterModels = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').filterModels
);

const PublicMetadata = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').publicMetadata
);

const InternalMetadata = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').internalMetadata
);

const SubscriptionUserPermissions = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').permissions
);

const PublishedSubscription = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').published
);

const PublishedSubscriptionStatus = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').published.status
);

const HistorySubscription = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').history
);

const MembersAccessSubscription = createSelector(
	baseSelector,
	(state) => state.get('inViewSubscription').membersAccess
);

@Injectable()
export class SubscriptionSelector {
	public constructor(private readonly store: Store<SubscriptionRootState>) {}

	private get state(): Observable<SubscriptionOriginalSliceState> {
		return this.store.pipe(select(baseSelector));
	}

	public getLoading$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('loading')));
	}

	public getSubscriptions$(): Observable<Map<string, SubscriptionItemEntity>> {
		return this.state.pipe(map((state) => state.get('subscriptions')));
	}

	public getTotalSubscriptions$(): Observable<number> {
		return this.state.pipe(map((state) => state.get('totalItems')));
	}

	public getHasCreatePermission$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('hasCreatePermission')));
	}

	public getProjects$(): Observable<Map<string, ProjectItemEntity>> {
		return this.state.pipe(map((state) => state.get('projects')));
	}

	public getSubscriptionView$(): Observable<ViewDetailsEntity> {
		return this.state.pipe(map((state) => state.get('inViewSubscription')));
	}

	public getSubscriptionId$(): Observable<number> {
		return this.state.pipe(map((state) => state.get('inViewSubscription').id));
	}

	public getInternalMetadata$(): Observable<InternalMetadataEntity> {
		return this.store.pipe(select(InternalMetadata));
	}

	public getPublicMetadata$(): Observable<PublicMetadataEntity> {
		return this.store.pipe(select(PublicMetadata));
	}

	public getSubscriptionUserPermissions$(): Observable<Set<ElementPermission>> {
		return this.store.pipe(select(SubscriptionUserPermissions));
	}

	public getPublishedSubscription$(): Observable<PublishedSubscriptionEntity> {
		return this.store.pipe(select(PublishedSubscription));
	}

	public getSubscriptionStatus$(): Observable<SubscriptionStatus> {
		return this.store.pipe(select(PublishedSubscriptionStatus));
	}

	public getHistorySubscription$(): Observable<Map<string, HistoryItemEntity>> {
		return this.store.pipe(select(HistorySubscription));
	}

	public getMembersAccessSubscription$(): Observable<
		Map<string, MemberAccessEntity>
	> {
		return this.store.pipe(select(MembersAccessSubscription));
	}

	public getActionInProgress$(): Observable<SubscriptionAction> {
		return this.state.pipe(
			map((state) => state.get('inViewSubscription').actionInProgress)
		);
	}

	public getSubscriptionColumns$(): Observable<
		Map<string, ColumnConfigItemEntity>
	> {
		return this.store.pipe(select(SubscriptionColumns));
	}

	public getSubscriptionProfiles$(): Observable<
		Map<string, ProfileItemEntity>
	> {
		return this.store.pipe(select(SubscriptionProfiles));
	}

	public getSubscriptionColumnModels$(): Observable<
		Map<string, ColumnModelItemEntity>
	> {
		return this.store.pipe(select(SubscriptionColumnModels));
	}

	public getSubscriptionFilterModels$(): Observable<
		Map<string, FilterModelItemEntity>
	> {
		return this.store.pipe(select(SubscriptionFilterModels));
	}

	public getConsumedProjects$(): Observable<
		Map<string, ConsumedProjectItemEntity>
	> {
		return this.store.pipe(select(SubscriptionProjects));
	}

	public getSwitchableConsumedProjects$(): Observable<
		Map<string, SwitchableVersionProjectItemEntity>
	> {
		return this.store.pipe(select(SubscriptionSwitchableProjects));
	}

	public isSwitchingVersion$(): Observable<boolean> {
		return this.state.pipe(map((state) => state.get('isSwitchingVersion')));
	}
}
